<template>
  <div class="col-12 col-xl-12">
    <div
      class="card mb-3 mb-xxl-5 me-xxl-4"
      style="margin-top: 1rem; margin-top: 4rem; overflow: visible"
    >
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 50px; height: 60px"
      >
        <div
          class="bg-gradient-primary shadow-primary border-radius-lg pt-3 pb-2 pt-4 pb-3 h-100"
        >
          <h6
            class="text-white text-capitalize ps-3 pe-3"
            style="font-size: 0.9rem; font-size: 1rem"
          >
            {{ $t("users.table.title") }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading && users.length === 0"
            :message="$t('users.table.loading')"
            class="py-3 py-5"
          />

          <template v-else>
            <div class="d-block d-xl-none">
              <div
                v-if="!loading && users.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-users-slash fa-2x mb-2"></i>
                <p>{{ $t("users.table.noUsers") }}</p>
              </div>

              <div
                v-for="user in users"
                :key="'mobile-' + user.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="avatar-container me-2">
                      <img
                        :src="getAvatarUrl(user)"
                        class="user-avatar rounded-circle"
                        style="width: 40px; height: 40px"
                        :alt="$t('users.table.avatarAlt')"
                        @error="handleImageError"
                      />
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-sm">{{ user.name }}</h6>
                      <p class="text-xs text-muted mb-0">{{ user.email }}</p>
                    </div>
                    <div>
                                <span class="badge bg-gradient-light text-dark">
  {{ plainMoney(user.salary) }} {{ $t('common.currency') }}
</span>

                    </div>
                    <div>
                      <span
                        class="badge badge-sm text-primary"
                        :class="getRoleBadgeClass(user.role_id)"
                      >
                        {{ getRoleName(user) }}
                      </span>
                    </div>
                  </div>

                  <div
                    class="d-flex justify-content-between align-items-center mt-2"
                  >
                    <span
                      class="badge badge-sm"
                      :class="
                        user.gender === 'male' ? 'bg-primary' : 'bg-secondary'
                      "
                    >
                      {{
                        user.gender === "male"
                          ? $t("users.table.male")
                          : $t("users.table.female")
                      }}
                    </span>

                    <div class="d-inline-flex">
                      <button
                        @click="navigateToUserDetails(user.id)"
                        class="btn btn-link text-primary p-0 mx-1"
                        :title="$t('users.actions.view')"
                      >
                        <i class="fas fa-eye fa-xs"></i>
                      </button>
                      <button
                        @click="navigateToEditUser(user.id)"
                        class="btn btn-link text-secondary p-0 mx-1"
                        :title="$t('users.actions.edit')"
                      >
                        <i class="fas fa-edit fa-xs"></i>
                      </button>
                      <button
                        @click="openDeleteDialog(user.id, user.name)"
                        class="btn btn-link text-danger p-0 mx-1"
                        :title="$t('users.actions.delete')"
                        :disabled="isProcessing"
                      >
                        <i class="fas fa-trash fa-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("users.table.avatar") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("users.table.name") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7"
                  >
                    {{ $t("users.table.email") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                  >
                    {{ $t("users.table.gender") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                  >
                    {{ $t("users.table.role") }}
                  </th>
                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center"
                  >
                    {{ $t("users.table.salary") }}
                  </th>

                  <th
                    class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4"
                  >
                    {{ $t("users.table.actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && users.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    <i class="fas fa-users-slash fa-2x mb-2"></i>
                    <p>{{ $t("users.table.noUsers") }}</p>
                  </td>
                </tr>

                <template v-if="!loading">
                  <tr v-for="user in users" :key="user.id">
                    <td>
                      <div class="avatar-container">
                        <img
                          :src="getAvatarUrl(user)"
                          class="user-avatar rounded-circle"
                          :alt="$t('users.table.avatarAlt')"
                          @error="handleImageError"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ user.name }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ user.email }}
                      </p>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="
                          user.gender === 'male' ? 'bg-primary' : 'bg-secondary'
                        "
                      >
                        {{
                          user.gender === "male"
                            ? $t("users.table.male")
                            : $t("users.table.female")
                        }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm text-primary"
                        :class="getRoleBadgeClass(user.role_id)"
                      >
                        {{ getRoleName(user) }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                    <span class="badge bg-gradient-light text-dark">
  {{ plainMoney(user.salary) }} {{ $t('common.currency') }}
</span>

                    </td>

                    <td class="align-middle text-end pe-4">
                      <div class="d-inline-flex">
                        <button
                          @click="navigateToUserDetails(user.id)"
                          class="btn btn-link text-primary p-1 mx-1"
                          :title="$t('users.actions.view')"
                        >
                          <i class="fas fa-eye fa-sm"></i>
                        </button>
                        <button
                          @click="navigateToEditUser(user.id)"
                          class="btn btn-link text-secondary p-1 mx-1"
                          :title="$t('users.actions.edit')"
                        >
                          <i class="fas fa-edit fa-sm"></i>
                        </button>
                        <button
                          @click="openDeleteDialog(user.id, user.name)"
                          class="btn btn-link text-danger p-1 mx-1"
                          :title="$t('users.actions.delete')"
                          :disabled="isProcessing"
                        >
                          <i class="fas fa-trash fa-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
  <DeleteDialog
    ref="deleteUserDialog"
    entity-name="User"
    :entity-label="$t('users.entity')"
    @delete="deleteUser"
  />
</template>

<script setup>
import { ref, defineProps, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import DeleteDialog from "@/components/common/DeleteDialog.vue";
import { useToast } from "vue-toastification";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();
const store = useStore();
const isProcessing = ref(false);
const router = useRouter();
const deleteUserDialog = ref(null);
const toast = useToast();

defineProps({
  users: {
    type: Array,
    required: true,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const roles = ref([]);
const getRoleName = (user) => {
  // If the role name is already a translated string, use it
  if (typeof user.role?.name === "string" && !user.role.name.startsWith("{")) {
    return user.role.name;
  }

  // If it's a JSON string, parse it and get the current locale
  try {
    const nameData =
      typeof user.role?.name === "string"
        ? JSON.parse(user.role.name)
        : user.role?.name;
    if (nameData && typeof nameData === "object") {
      return nameData[locale.value] || nameData.en || user.role?.name;
    }
    return user.role?.name || `الدور ${user.role_id || "غير محدد"}`;
  } catch (e) {
    return user.role?.name || `الدور ${user.role_id || "غير محدد"}`;
  }
};
const plainMoney = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toString();   
};

onMounted(async () => {
  try {
    await store.dispatch("fetchRoles");
    roles.value = store.getters.getRoles || [];
  } catch (error) {
    console.error("Failed to fetch roles:", error);
    toast.error("فشل في تحميل الأدوار");
  }
});

const getAvatarUrl = (user) => {
  if (user.avatar_url) {
    return user.avatar_url.includes("http")
      ? user.avatar_url
      : `${import.meta.env.VITE_API_BASE_URL || ""}${user.avatar_url}`;
  }
  return "/images/default-avatar.png";
};

const getRoleBadgeClass = (roleId) => {
  const classes = {
    1: "bg-gradient-danger",
    2: "bg-gradient-info",
    3: "bg-gradient-warning",
    4: "bg-gradient-primary",
    5: "bg-gradient-success",
    6: "bg-gradient-secondary",
  };
  return classes[roleId] || "bg-gradient-dark";
};

const deleteUser = async (userId) => {
  isProcessing.value = true;
  try {
    await store.dispatch("deleteUser", userId);
    toast.success("تم حذف المستخدم بنجاح");
  } catch (error) {
    console.error("Delete user error:", error);
    toast.error(error.response?.data?.message || "فشل في حذف المستخدم");
  } finally {
    isProcessing.value = false;
  }
};

const openDeleteDialog = (id, name) => {
  deleteUserDialog.value.openDialog(id, name);
};
const navigateToUserDetails = (userId) => {
  router.push(`/users/${userId}`);
};
const navigateToEditUser = (userId) => {
  router.push(`/users/${userId}/edit`);
};
</script>
